import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { AppState } from "src/app/reducers";
import { CumplesService } from "../../services/cumples.service";
import { CumplesActionTypes, CumplesCancelled, CumplesLoaded, CumplesRequested, CumplesInitFilterCancelled, CumplesInitFilterLoaded, CumplesInitFilterRequested, MensajeCumpleCancelled, MensajeCumpleCreated, MensajeCumpleRequested, UsuarioFotoCancelled, UsuarioFotoDoNothing, UsuarioFotoLoaded, UsuarioFotoRequested, CumplesFilterChangeRequested, CumplesFilterChangeDone, UsuarioFuncionesRequested, UsuarioFuncionesCancelled, UsuarioFuncionesLoaded } from "./cumples.actions";
import { catchError, map, switchMap, mergeMap, withLatestFrom } from 'rxjs/operators';
import { Observable, of } from "rxjs";
import { ToastService } from "src/app/services/toast.service";
import { CumpleFilterDates, MensajeCumple, UsuarioCumple } from "src/common/cumples";
import { cumplesEntityFunc, cumplesFilterActualDate, cumplesFilterDates, cumplesFuncionesIdUsuarioMf } from "./cumples.selectors";
import { HttpErrorResponse } from "@angular/common/http";

@Injectable()
export class CumplesEffects {

	private readonly NOT_FOUND: number = 404;


	cumplesRequested$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<CumplesRequested>( CumplesActionTypes.CumplesRequested ),
			withLatestFrom( this.store.select( cumplesFilterDates ) ),
			switchMap( ([ action, filterDates ]) => {
				return this.service.findCumplesByDate( filterDates.startDate ).pipe(
						map( cumples => new CumplesLoaded( { cumples } ) ),
						catchError( error => {
							if( error instanceof HttpErrorResponse && error.status != this.NOT_FOUND )
								this.toastService.danger( "No se pudieron recuperar los cumpleaños" );
							return of( new CumplesCancelled() );
						})
					);
			})
		);
	});

	mensajeCumpleRequested$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<MensajeCumpleRequested>( CumplesActionTypes.MensajeCumpleRequested ),
			withLatestFrom( this.store.select( cumplesFilterDates ) ),
			switchMap( ([ action, filterDates ]) => {
				let mensajeCumple: MensajeCumple = action["payload"];
				mensajeCumple = { ...mensajeCumple, date: filterDates.startDate };
				return this.service.enviarMensajeCumple( mensajeCumple ).pipe(
				 	map( mensajeCumple => new MensajeCumpleCreated( mensajeCumple ) ),
				 	catchError( error => {
				 		this.toastService.danger( "No se pudo enviar el mensaje de cumpleaños" );
				 		return of( new MensajeCumpleCancelled() );
				 	})
				);
			})
		);
	});

	cumplesLoaded$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<CumplesLoaded>( CumplesActionTypes.CumplesLoaded ),
			switchMap( action => {
				let cumples: UsuarioCumple[] = action.payload.cumples;
				let result: Action[] = [];
				// Para cada idUsuario de cumples genero un action para recuperar la foto
				if( cumples && cumples.length > 0 )
					cumples.forEach( cumple => result.push( new UsuarioFotoRequested( { idUsuarioMf: cumple.idUsuarioMf } ) ) );
				else
					result = [ new UsuarioFotoDoNothing() ];
				return result;
			})
		);
	});

	usuarioFotoRequested$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<UsuarioFotoRequested>( CumplesActionTypes.UsuarioFotoRequested ),
			withLatestFrom( this.store.select( cumplesEntityFunc ) ),
			mergeMap( ([ action, cumplesEntityFunc ]) => {
				let result: Observable<Action> = null;
				let idUsuarioMf: number = action["payload"]["idUsuarioMf"];
				if( idUsuarioMf ) {
					let cumple: UsuarioCumple = cumplesEntityFunc( idUsuarioMf );
					if( cumple && cumple.idPersona )
						result = this.service.findUsuarioFoto( cumple.idPersona )
							.pipe(
								map( foto => new UsuarioFotoLoaded( { idUsuarioMf, foto: foto.foto } ) ),
								catchError( () => of( new UsuarioFotoCancelled( { idUsuarioMf } ) ) )
							);
				}

				if( !result )
					result = of( new UsuarioFotoCancelled( { idUsuarioMf } ) );

				return result;
			})
		);
	});

	initFilterRequested$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<CumplesInitFilterRequested>( CumplesActionTypes.CumplesInitFilterRequested ),
			withLatestFrom( this.store.select( cumplesFilterDates ) ),
			switchMap( ([ action, filterDates ]) => {
				return filterDates? of( new CumplesInitFilterCancelled() ):
					this.service.getFilterDates()
						.pipe(
							map( dates => {
								let filterDates: CumpleFilterDates = dates;
								return new CumplesInitFilterLoaded( { filterDates } );
							}),
							catchError( () => {
								this.toastService.danger( "No se pudo inicializar el calendario de cumpleaños" );
								return of( new CumplesInitFilterCancelled() );
							})
						);
			})
		);
	});

	filterDateChanged$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<CumplesFilterChangeRequested>( CumplesActionTypes.CumplesFilterChangeRequested ),
			withLatestFrom( this.store.select( cumplesFilterActualDate ) ),
			switchMap( ([ action, filterActualDate ]) => {
				let actions: Action[] = [ new CumplesFilterChangeDone( { date: action.payload.date } ) ];
				if( filterActualDate != action.payload.date )
					actions.push( new CumplesRequested() );
				return actions;
			})
		);
	});

	usuarioFuncionesRequested$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<UsuarioFuncionesRequested>( CumplesActionTypes.UsuarioFuncionesRequested ),
			withLatestFrom( this.store.select( cumplesFuncionesIdUsuarioMf ) ),
			switchMap( ([ action, idUsuarioMf ]) => {
				return action.payload.idUsuarioMf == idUsuarioMf? of( new UsuarioFuncionesCancelled() ):
					this.service.getUsuarioFunciones( action.payload.idUsuarioMf )
						.pipe(
							map( funciones => new UsuarioFuncionesLoaded( { funciones, idUsuarioMf: action.payload.idUsuarioMf } ) ),
							catchError( () => {
								this.toastService.danger( "No se pudieron obtener las funciones de la persona seleccionada" );
								return of( new UsuarioFuncionesCancelled() );
							})
						);
			})
		);
	});


	constructor( private actions$: Actions, private store: Store<AppState>, private service: CumplesService, private toastService: ToastService ) {
	}

}